import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Container, Card, Row, Col, Badge, Alert, Tooltip, OverlayTrigger, Modal, Image, ListGroup, Form } from 'react-bootstrap';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { 
  FaHome, 
  FaUserInjured, 
  FaUserMd, 
  FaPills, 
  FaFileMedical,
  FaCalendarCheck,
  FaFileInvoiceDollar,
  FaListOl,
  FaChartLine,
  FaCog,
  FaMoneyBillWave,
  FaReceipt
} from 'react-icons/fa';

const Users = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [userForAccess, setUserForAccess] = useState(null);
  
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const res = await axios.get('/api/users.php');
        setUsers(res.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.error || 'Error fetching users');
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, []);
  
  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      setError(null); // Clear any previous errors
      setSuccessMessage(null);
      const res = await axios.put(`/api/users.php?id=${userId}`, {
        id: userId,
        isActive: !currentStatus
      });
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, isActive: !currentStatus } : user
      ));
      
      setSuccessMessage(`User status updated successfully`);
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating user status');
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setError(null);
      setSuccessMessage(null);
      await axios.delete(`/api/users.php?id=${userToDelete.id}`);
      
      // Remove user from state
      setUsers(users.filter(user => user.id !== userToDelete.id));
      setShowDeleteModal(false);
      setUserToDelete(null);
      setSuccessMessage('User deleted successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting user');
      setShowDeleteModal(false);
    }
  };
  
  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge bg="danger">Admin</Badge>;
      case 'doctor':
        return <Badge bg="success">Doctor</Badge>;
      case 'receptionist':
        return <Badge bg="primary">Receptionist</Badge>;
      default:
        return <Badge bg="secondary">{role}</Badge>;
    }
  };
  
  // Render button with tooltip for admin users
  const renderActionButton = (user) => {
    // If user is admin, disable the deactivate button and show tooltip
    if (user.role === 'admin') {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`tooltip-${user.id}`}>Admin users cannot be deactivated</Tooltip>}
        >
          <span className="d-inline-block">
            <Button
              variant={user.isActive ? 'outline-danger' : 'outline-success'}
              size="sm"
              disabled={user.isActive} // Disable only for active admins
              onClick={() => !user.isActive && toggleUserStatus(user.id, user.isActive)}
            >
              {user.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </span>
        </OverlayTrigger>
      );
    }
    
    // For non-admin users, show regular button
    return (
      <Button
        variant={user.isActive ? 'outline-danger' : 'outline-success'}
        size="sm"
        onClick={() => toggleUserStatus(user.id, user.isActive)}
      >
        {user.isActive ? 'Deactivate' : 'Activate'}
      </Button>
    );
  };

  // Render delete button with tooltip for admin users
  const renderDeleteButton = (user) => {
    if (user.role === 'admin') {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`delete-tooltip-${user.id}`}>Admin users cannot be deleted</Tooltip>}
        >
          <span className="d-inline-block">
            <Button
              variant="danger"
              size="sm"
              disabled
              className="ms-2"
            >
              Delete
            </Button>
          </span>
        </OverlayTrigger>
      );
    }

    return (
      <Button
        variant="danger"
        size="sm"
        className="ms-2"
        onClick={() => handleDeleteClick(user)}
      >
        Delete
      </Button>
    );
  };

  // Handle view details
  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let pass = '';
    for (let i = 0; i < 8; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  };

  // Handle reset password
  const handleResetPassword = (user) => {
    setSelectedUser(user);
    const autoPass = generatePassword();
    setNewPassword(autoPass);
    setGeneratedPassword('');
    setShowPasswordModal(true);
  };

  // Submit password reset
  const handlePasswordReset = async () => {
    try {
      await axios.put(`/api/users.php?id=${selectedUser.id}`, {
        id: selectedUser.id,
        password: newPassword
      });
      setGeneratedPassword(newPassword);
      setSuccessMessage(`Password reset for ${selectedUser.username}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  // Render view details button
  const renderViewButton = (user) => {
    return (
      <>
        <Button
          variant="primary"
          size="sm"
          className="me-2"
          onClick={() => handleViewDetails(user)}
        >
          View
        </Button>
        <Button
          variant="warning"
          size="sm"
          className="me-2"
          onClick={() => handleResetPassword(user)}
        >
          Reset Password
        </Button>
      </>
    );
  };

  // Render edit button for users
  const renderEditButton = (user) => {
    return (
      <Button
        variant="info"
        size="sm"
        className="me-2"
        as={Link}
        to={`/users/edit/${user.id}`}
      >
        Edit
      </Button>
    );
  };

  // Render action buttons for each user
  const renderActionButtons = (userItem) => {
    return (
      <>
        {user?.role === 'admin' && renderViewButton(userItem)}
        {renderEditButton(userItem)}
        <Button
          variant="outline-info"
          size="sm"
          className="me-2"
          as={Link}
          to={`/user-access/${userItem.id}`}
        >
          Access
        </Button>
        {renderActionButton(userItem)}
        {renderDeleteButton(userItem)}
      </>
    );
  };

  // Render user profile image or default avatar
  const renderUserAvatar = (user) => {
    if (user.profileImage) {
      return (
        <Image 
          src={user.profileImage} 
          roundedCircle 
          width={40} 
          height={40} 
          style={{ objectFit: 'cover' }} 
        />
      );
    } else {
      return (
        <div 
          style={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            backgroundColor: '#e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            color: '#6c757d'
          }}
        >
          {user.username.charAt(0).toUpperCase()}
        </div>
      );
    }
  };
  
  if (loading) {
    return (
      <Container className="my-4">
        <h2>Loading users...</h2>
      </Container>
    );
  }
  
  return (
    <Container className="my-4">
      <Row className="mb-4">
        <Col>
          <h2>Users Management</h2>
        </Col>
        <Col xs="auto">
          <Link to="/register">
            <Button variant="success" size="lg">
              <span className="me-2">+</span> Add New User
            </Button>
          </Link>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}
      
      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Avatar</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Doctor</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user, index) => (
                  <tr key={user.id} className="align-middle">
                    <td>{index + 1}</td>
                    <td>{renderUserAvatar(user)}</td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{getRoleBadge(user.role)}</td>
                    <td>{user.Doctor ? user.Doctor.name : '-'}</td>
                    <td>
                      <Badge bg={user.isActive ? 'success' : 'danger'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      {renderActionButtons(user)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* User Details Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <div className="text-center mb-4">
                {renderUserAvatar(selectedUser)}
                <h4 className="mt-3">{selectedUser.username}</h4>
                {getRoleBadge(selectedUser.role)}
              </div>
              
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>User ID:</strong> {selectedUser.id}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Username:</strong> {selectedUser.username}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Email:</strong> {selectedUser.email}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Role:</strong> {selectedUser.role}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Status:</strong>{' '}
                  <Badge bg={selectedUser.isActive ? 'success' : 'danger'}>
                    {selectedUser.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </ListGroup.Item>
                {selectedUser.Doctor && (
                  <ListGroup.Item>
                    <strong>Linked Doctor:</strong> {selectedUser.Doctor.name}
                    <br />
                    <small className="text-muted">
                      Specialization: {selectedUser.Doctor.specialization}
                    </small>
                  </ListGroup.Item>
                )}
                <ListGroup.Item>
                  <strong>Created:</strong> {new Date(selectedUser.createdAt).toLocaleString()}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Last Updated:</strong> {new Date(selectedUser.updatedAt).toLocaleString()}
                </ListGroup.Item>
              </ListGroup>
              
              <Alert variant="warning" className="mt-3">
                <strong>Security Note:</strong> Passwords are encrypted and cannot be displayed. Use "Reset Password" button to set a new password.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          {selectedUser && (
            <Button 
              variant="primary" 
              as={Link} 
              to={`/users/edit/${selectedUser.id}`}
              onClick={() => setShowDetailModal(false)}
            >
              Edit User
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Password Reset Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header>
          <Modal.Title>Reset Password - {selectedUser?.username}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!generatedPassword ? (
            <>
              <Alert variant="info">
                A random password has been generated. You can edit it or use as is.
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </Form.Group>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setNewPassword(generatePassword())}
              >
                Generate New
              </Button>
            </>
          ) : (
            <Alert variant="success">
              <h5>Password Reset Successful!</h5>
              <hr />
              <p><strong>Username:</strong> {selectedUser?.username}</p>
              <p><strong>New Password:</strong> <code style={{fontSize: '1.2em', background: '#fff', padding: '5px 10px', borderRadius: '4px'}}>{generatedPassword}</code></p>
              <hr />
              <small>⚠️ Save this password now. It cannot be retrieved later.</small>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!generatedPassword ? (
            <>
              <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handlePasswordReset}>
                Reset Password
              </Button>
            </>
          ) : (
            <Button variant="success" onClick={() => setShowPasswordModal(false)}>
              Done
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the user <strong>{userToDelete?.username}</strong>?
          <p className="mt-2 text-danger">
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete User
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Access Control Modal */}
      <Modal show={showAccessModal} onHide={() => setShowAccessModal(false)} size="xl" fullscreen>
        <Modal.Header>
          <Modal.Title>Manage Access Permissions - {userForAccess?.username}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: 0, backgroundColor: '#f8f9fa'}}>
          <Row style={{height: '100%', margin: 0}}>
            {/* Sidebar Preview with Checkboxes */}
            <Col md={3} style={{backgroundColor: '#2c3e50', padding: '20px', color: '#e0e0e0', maxHeight: '80vh', overflowY: 'auto'}}>
              <h6 className="text-white mb-3">Sidebar Menu</h6>
              
              <div className="d-flex align-items-center mb-2">
                <Form.Check type="checkbox" defaultChecked className="me-2" />
                <span><FaHome className="me-2" />Dashboard</span>
              </div>
              
              <div className="d-flex align-items-center mb-2">
                <Form.Check type="checkbox" defaultChecked className="me-2" />
                <span><FaUserInjured className="me-2" />Patients</span>
              </div>
              
              <div className="d-flex align-items-center mb-2">
                <Form.Check type="checkbox" defaultChecked className="me-2" />
                <span><FaUserMd className="me-2" />Doctors</span>
              </div>
              
              <div className="d-flex align-items-center mb-2">
                <Form.Check type="checkbox" defaultChecked className="me-2" />
                <span><FaCalendarCheck className="me-2" />Appointments</span>
              </div>
              
              <div className="d-flex align-items-center mb-2">
                <Form.Check type="checkbox" defaultChecked className="me-2" />
                <span><FaListOl className="me-2" />Patient Queue</span>
              </div>
              
              <div className="d-flex align-items-center mb-2">
                <Form.Check type="checkbox" defaultChecked className="me-2" />
                <span><FaPills className="me-2" />Medicines</span>
              </div>
              
              <div className="border-top border-secondary my-2"></div>
              
              <div className="mb-2">
                <div className="d-flex align-items-center mb-2">
                  <Form.Check type="checkbox" defaultChecked className="me-2" />
                  <span className="fw-bold"><FaFileMedical className="me-2" />Reports & Billing</span>
                </div>
                <div className="ms-4">
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Medical Reports</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Appointment Reports</span>
                  </div>
                </div>
              </div>
              
              <div className="mb-2">
                <div className="d-flex align-items-center mb-2">
                  <Form.Check type="checkbox" defaultChecked className="me-2" />
                  <span className="fw-bold"><FaFileInvoiceDollar className="me-2" />Finance</span>
                </div>
                <div className="ms-4">
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Billing</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Financial Reports</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Expenses</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Doctor Salaries</span>
                  </div>
                </div>
              </div>
              
              <div className="border-top border-secondary my-2"></div>
              
              <div className="mb-2">
                <div className="d-flex align-items-center mb-2">
                  <Form.Check type="checkbox" defaultChecked className="me-2" />
                  <span className="fw-bold"><FaCog className="me-2" />Administration</span>
                </div>
                <div className="ms-4">
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Users</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Roles</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Settings</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <Form.Check type="checkbox" defaultChecked className="me-2" />
                    <span style={{fontSize: '0.9em'}}>Backup & Restore</span>
                  </div>
                </div>
              </div>
            </Col>
            
            {/* Main Content Area with Button Checkboxes */}
            <Col md={9} style={{padding: '20px', maxHeight: '80vh', overflowY: 'auto'}}>
              <h6 className="mb-3">Page Actions & Buttons</h6>
              
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <strong>Patient Management</strong>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="primary">+ Add Patient</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="warning">Edit Patient</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="danger">Delete Patient</Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <strong>Billing & Payments</strong>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="success">Create Bill</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="info">Edit Bill</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="danger">Delete Bill</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="success">Process Payment</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="warning">Cancel & Refund</Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <strong>Queue Management</strong>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="primary">Add to Queue</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="success">Complete</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="info">Print Ticket</Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <strong>Reports & Documents</strong>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="primary">Create Report</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="danger">Delete Report</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="info">Print Report</Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <strong>Doctor & Medicine Management</strong>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="primary">Add Doctor</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="warning">Edit Doctor</Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check type="checkbox" defaultChecked className="me-2" />
                        <Button size="sm" variant="primary">Add Medicine</Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAccessModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => {
            setSuccessMessage('Access permissions updated successfully');
            setShowAccessModal(false);
          }}>
            Save Access Permissions
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Users; 
